import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Award, Heart, MapPin, Wrench, TreePine } from 'lucide-react';
import { companyInfo, gallery } from '../data/mock';
import SEO from '../components/SEO';

const Home = () => {
  const videoRef = useRef(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  // Video URL
  const heroVideoUrl = "https://customer-assets-v7afamib.emergentagent.net/job_f5435fcc-fe09-489a-80a2-71deea7069e0/artifacts/njlx3fg3_vid%C3%A9o%20site%20internet%20%281%29%20%281%29.mp4";
  
  // Poster image (fallback)
  const posterImage = "https://customer-assets.emergentagent.com/job_arborist-services/artifacts/g9it91th_20250724_164248.jpg";

  // Sélection des images pour chaque catégorie
  const paysagementImage = "https://customer-assets.emergentagent.com/job_arborist-services/artifacts/g9it91th_20250724_164248.jpg";
  const arboricultureImage = "https://customer-assets.emergentagent.com/job_972aa941-b618-44c7-b555-7700fb81204b/artifacts/ihonpgi4_Alex%202.jpg";
  const forestierImage = "https://customer-assets.emergentagent.com/job_972aa941-b618-44c7-b555-7700fb81204b/artifacts/m1t2zcr9_53c55735-3ddf-465f-9df7-59a1e6963531.jpg";
  
  // Images pour la galerie de réalisations
  const featuredRealisations = [
    { image: paysagementImage, title: "Paysagement résidentiel", category: "Paysagement" },
    { image: arboricultureImage, title: "Élagage en hauteur", category: "Arboriculture" },
    { image: forestierImage, title: "Déboisement forestier", category: "Travaux forestiers" },
    { image: "https://customer-assets.emergentagent.com/job_arborist-services/artifacts/2l1t9hxm_Screenshot_20250212_210136_Gallery.jpg", title: "Plantation de haies", category: "Paysagement" },
    { image: "https://customer-assets.emergentagent.com/job_arborist-services/artifacts/v7r7q29n_81f75804-7938-43ab-ad75-e270ffb00067.jpg", title: "Abattage d'arbres", category: "Arboriculture" },
    { image: "https://customer-assets.emergentagent.com/job_arborist-services/artifacts/nrd092ig_57daffdd-665c-4514-971a-9b5d2f076a36.jpg", title: "Essouchage", category: "Arboriculture" },
  ];

  // Check for prefers-reduced-motion
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Ensure video plays on mobile
  useEffect(() => {
    if (videoRef.current && !prefersReducedMotion) {
      videoRef.current.play().catch(() => {
        // Autoplay was prevented, user will see poster
      });
    }
  }, [prefersReducedMotion]);

  return (
    <div className="page-home-new">
      <SEO 
        title="PaysFor | Paysagement, Arboriculture & Travaux forestiers à Québec"
        description="PaysFor accompagne particuliers et entreprises dans leurs projets de paysagement, d'abattage d'arbres, d'élagage et de travaux forestiers à Québec et dans la Capitale-Nationale. Soumission gratuite."
        keywords="paysagement Québec, aménagement paysager Québec, abattage d'arbres Québec, élagage Québec, arboriculture Québec, travaux forestiers Québec, plantation arbres Québec, Capitale-Nationale"
        canonical="/"
      />

      {/* ========================================
          HERO SECTION WITH VIDEO BACKGROUND
          ======================================== */}
      <section className="hero-video-section">
        {/* Video Background */}
        <div className="hero-video-container">
          {!prefersReducedMotion ? (
            <video
              ref={videoRef}
              className="hero-video"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              poster={posterImage}
            >
              <source src={heroVideoUrl} type="video/mp4" />
            </video>
          ) : (
            <img 
              src={posterImage} 
              alt="PaysFor - Paysagement, Arboriculture et Travaux forestiers" 
              className="hero-video-poster"
            />
          )}
          {/* Dark overlay for text readability */}
          <div className="hero-video-overlay"></div>
        </div>

        {/* Hero Content */}
        <div className="hero-video-content">
          <div className="container">
            {/* Tagline supérieur */}
            <span className="hero-tagline-video">
              PAYSAGEMENT • ARBORICULTURE • TRAVAUX FORESTIERS
            </span>
            
            {/* Titre principal */}
            <h1 className="hero-title-video">
              Votre terrain, notre expertise.
            </h1>
            
            {/* Description */}
            <p className="hero-description-video">
              PaysFor accompagne particuliers et entreprises dans leurs projets de paysagement, 
              de travaux arboricoles et de travaux forestiers dans la région de Québec. 
              De l'aménagement et la plantation jusqu'à l'élagage, l'abattage et aux interventions 
              en milieu forestier, notre équipe offre des solutions professionnelles adaptées à chaque terrain.
            </p>
            
            {/* Boutons CTA */}
            <div className="hero-buttons-video">
              <Link to="/contact" className="btn-cta-video">
                Demander une soumission
                <ArrowRight size={20} />
              </Link>
              <a href="#expertises" className="btn-secondary-video">
                Découvrir nos expertises
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================
          SECTION 3 EXPERTISES
          ======================================== */}
      <section className="expertises-section" id="expertises">
        <div className="container">
          <div className="expertises-header">
            <h2 className="expertises-title">Trois expertises pour votre terrain</h2>
            <p className="expertises-subtitle">
              Que vous souhaitiez aménager, entretenir ou sécuriser votre terrain, 
              PaysFor réunit les compétences nécessaires sous une même entreprise.
            </p>
          </div>

          <div className="expertises-grid">
            {/* CARTE 1 - PAYSAGEMENT */}
            <div className="expertise-card expertise-paysagement">
              <div className="expertise-image">
                <img src={paysagementImage} alt="Services de paysagement à Québec" loading="lazy" />
              </div>
              <div className="expertise-content">
                <h3 className="expertise-title">Paysagement</h3>
                <p className="expertise-description">
                  Aménagement, plantation et mise en valeur de vos espaces extérieurs.
                </p>
                <ul className="expertise-services">
                  <li>Plantation d'arbres et de cèdres</li>
                  <li>Aménagement paysager</li>
                  <li>Plates-bandes</li>
                  <li>Paillis et bordures</li>
                  <li>Préparation de terrain</li>
                </ul>
                <Link to="/paysagement" className="expertise-cta">
                  Découvrir le paysagement
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            {/* CARTE 2 - ABATTAGE & ÉLAGAGE */}
            <div className="expertise-card expertise-arboriculture">
              <div className="expertise-image">
                <img src={arboricultureImage} alt="Abattage et élagage d'arbres à Québec" loading="lazy" />
              </div>
              <div className="expertise-content">
                <h3 className="expertise-title">Abattage & Élagage</h3>
                <p className="expertise-description">
                  Des interventions arboricoles professionnelles pour entretenir, sécuriser ou retirer vos arbres.
                </p>
                <ul className="expertise-services">
                  <li>Abattage</li>
                  <li>Élagage</li>
                  <li>Démontage</li>
                  <li>Taille et rabaissement</li>
                  <li>Essouchage</li>
                </ul>
                <Link to="/abattage-elagage" className="expertise-cta">
                  Découvrir nos services arboricoles
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            {/* CARTE 3 - TRAVAUX FORESTIERS */}
            <div className="expertise-card expertise-forestier">
              <div className="expertise-image">
                <img src={forestierImage} alt="Travaux forestiers et déboisement à Québec" loading="lazy" />
              </div>
              <div className="expertise-content">
                <h3 className="expertise-title">Travaux forestiers</h3>
                <p className="expertise-description">
                  Des solutions adaptées aux boisés, terrains forestiers et projets nécessitant des travaux spécialisés.
                </p>
                <ul className="expertise-services">
                  <li>Déboisement</li>
                  <li>Débroussaillage</li>
                  <li>Dégagement de plantations</li>
                  <li>Préparation de terrain</li>
                  <li>Travaux en boisé</li>
                  <li>Chemins forestiers</li>
                </ul>
                <Link to="/travaux-forestiers" className="expertise-cta">
                  Découvrir les travaux forestiers
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================
          SECTION IDENTITÉ PAYSFOR
          ======================================== */}
      <section className="identity-section">
        <div className="container">
          <div className="identity-content">
            <div className="identity-formula">
              <span className="identity-word">PAYS</span>
              <span className="identity-meaning">agement</span>
              <span className="identity-plus">+</span>
              <span className="identity-word">FOR</span>
              <span className="identity-meaning">esterie</span>
              <span className="identity-equals">=</span>
              <span className="identity-brand">PAYSFOR</span>
            </div>
            <p className="identity-text">
              PaysFor réunit sous une même entreprise l'expertise du paysagement, de l'arboriculture 
              et des travaux forestiers. Une approche complète pour aménager, entretenir, sécuriser 
              et valoriser votre terrain.
            </p>
          </div>
        </div>
      </section>

      {/* ========================================
          SECTION RÉALISATIONS
          ======================================== */}
      <section className="realisations-section">
        <div className="container">
          <div className="realisations-header">
            <h2 className="realisations-title">Nos réalisations</h2>
            <p className="realisations-subtitle">
              Un aperçu de nos projets de paysagement, d'arboriculture et de travaux forestiers 
              réalisés dans la région de Québec.
            </p>
          </div>

          <div className="realisations-grid">
            {featuredRealisations.map((item, index) => (
              <div key={index} className="realisation-card">
                <img src={item.image} alt={item.title} loading="lazy" />
                <div className="realisation-overlay">
                  <span className="realisation-category">{item.category}</span>
                  <span className="realisation-title">{item.title}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="realisations-cta">
            <Link to="/realisations" className="btn-secondary-large">
              Voir toutes nos réalisations
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* ========================================
          SECTION POURQUOI CHOISIR PAYSFOR
          ======================================== */}
      <section className="why-section">
        <div className="container">
          <div className="why-header">
            <h2 className="why-title">Pourquoi choisir PaysFor?</h2>
            <p className="why-subtitle">
              Une entreprise locale qui s'engage pour la qualité et la satisfaction de ses clients.
            </p>
          </div>

          <div className="why-grid">
            <div className="why-card-new">
              <div className="why-icon-new">
                <Wrench size={32} />
              </div>
              <h3>Expertise terrain</h3>
              <p>
                Une connaissance approfondie des arbres, des plantes et des terrains du Québec 
                pour des interventions adaptées.
              </p>
            </div>

            <div className="why-card-new">
              <div className="why-icon-new">
                <Shield size={32} />
              </div>
              <h3>Travail professionnel et sécuritaire</h3>
              <p>
                Des méthodes de travail rigoureuses et un équipement adapté pour garantir 
                sécurité et résultats de qualité.
              </p>
            </div>

            <div className="why-card-new">
              <div className="why-icon-new">
                <Heart size={32} />
              </div>
              <h3>Service personnalisé</h3>
              <p>
                Chaque projet est unique. Nous adaptons nos services à vos besoins 
                spécifiques et à la réalité de votre terrain.
              </p>
            </div>

            <div className="why-card-new">
              <div className="why-icon-new">
                <Award size={32} />
              </div>
              <h3>Respect de la propriété</h3>
              <p>
                Un souci constant de la propreté du chantier et du respect de votre 
                terrain et de vos aménagements existants.
              </p>
            </div>

            <div className="why-card-new">
              <div className="why-icon-new">
                <TreePine size={32} />
              </div>
              <h3>Équipement adapté</h3>
              <p>
                Des outils et équipements professionnels pour le paysagement, 
                l'arboriculture et les travaux forestiers.
              </p>
            </div>

            <div className="why-card-new">
              <div className="why-icon-new">
                <MapPin size={32} />
              </div>
              <h3>Service dans la région de Québec</h3>
              <p>
                Nous desservons Québec et la Capitale-Nationale avec possibilité de 
                déplacements pour les projets d'envergure.
              </p>
            </div>
          </div>

          {/* Image de confiance */}
          <div className="why-image-section">
            <img 
              src="https://customer-assets.emergentagent.com/job_arborist-services/artifacts/z5hz3z7j_Messenger_creation_6E8AF665-5AC2-4251-8B18-D1F12E63DCE8.jpeg"
              alt="Axel, fondateur de PaysFor - Service de qualité à Québec"
              className="why-image"
            />
          </div>
        </div>
      </section>

      {/* ========================================
          SECTION CTA FINAL
          ======================================== */}
      <section className="cta-final-section">
        <div className="container">
          <div className="cta-final-content">
            <h2 className="cta-final-title">Un projet pour votre terrain?</h2>
            <p className="cta-final-text">
              Paysagement, travaux arboricoles ou travaux forestiers : parlez-nous de votre projet 
              et obtenez une soumission adaptée à vos besoins.
            </p>
            <div className="cta-final-buttons">
              <Link to="/contact" className="btn-cta-final">
                Demander une soumission
                <ArrowRight size={20} />
              </Link>
              <a href={`tel:${companyInfo.phone}`} className="btn-phone-final">
                {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================
          CONTENU SEO ADDITIONNEL
          ======================================== */}
      <section className="seo-content-section">
        <div className="container">
          <div className="seo-content">
            <h2>Services de paysagement et d'arboriculture à Québec</h2>
            <p>
              PaysFor est une entreprise spécialisée en <strong>paysagement</strong>, 
              <strong>aménagement paysager</strong>, <strong>abattage d'arbres</strong>, 
              <strong>élagage</strong> et <strong>travaux forestiers</strong> dans la région de 
              <strong>Québec</strong> et de la <strong>Capitale-Nationale</strong>.
            </p>
            <p>
              Que vous ayez besoin d'un service de <strong>plantation d'arbres</strong>, 
              d'<strong>arboriculture professionnelle</strong>, de <strong>taille de haies</strong>, 
              d'<strong>essouchage</strong> ou de <strong>déboisement</strong>, notre équipe 
              vous accompagne dans tous vos projets d'aménagement et d'entretien de terrain.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
